import React from 'react'
import { Box, Grid } from '@mui/material'
import { HeatColor, formatPrice } from '@ui-seeker/utils'
import { AppButtonIcon, AppDataTable, AppRetailerBlock } from '@components'
import { dsColorNew } from "@palette"

import { dorColumn, staticColumn } from 'Pages/RealTimeHistory/RealTime/Table/@columns'
import { COLUMN_PARAMS_LIST } from 'Pages/RealTimeHistory/RealTime/@constants'
import RealTimeTableTitle from 'Pages/RealTimeHistory/RealTime/Table/Title'
import RealTimeTableErrorsData from 'Pages/RealTimeHistory/RealTime/ErrorData'
import { idSearch } from 'Pages/RealTimeHistory/RealTime/@signals'


const COUNTRY = { ES: 'Spain' }

const DEMO_RESULTS = [
	{ website: 'rentalspot.demo', flag: 'ES', dor: '2025-03-15', lor: 5, car: 'Fiat 500 or similar', acriss: 'ECMR', transmission: 'Manual', category: 'Economy', mileage: 'Unlimited', provider: 'FleetMax', payment: 'Prepaid', price: 42.30 },
	{ website: 'pricefly.demo', flag: 'ES', dor: '2025-03-15', lor: 5, car: 'Seat Ibiza or similar', acriss: 'ECMR', transmission: 'Manual', category: 'Economy', mileage: 'Unlimited', provider: 'CarFirst', payment: 'Prepaid', price: 48.75 },
	{ website: 'bookride.demo', flag: 'ES', dor: '2025-03-15', lor: 5, car: 'Opel Corsa or similar', acriss: 'EBMR', transmission: 'Manual', category: 'Economy', mileage: 'Unlimited', provider: 'ParkDrive', payment: 'Pay at desk', price: 55.20 },
	{ website: 'eurodrive.demo', flag: 'ES', dor: '2025-03-15', lor: 5, car: 'Toyota Yaris or similar', acriss: 'ECMR', transmission: 'Manual', category: 'Economy', mileage: 'Unlimited', provider: 'DrivePro', payment: 'Prepaid', price: 61.90 },
	{ website: 'findwheels.demo', flag: 'ES', dor: '2025-03-15', lor: 5, car: 'VW Polo or similar', acriss: 'EDMR', transmission: 'Manual', category: 'Economy', mileage: 'Unlimited', provider: 'Velocar', payment: 'Pay at desk', price: 68.00 },
	{ website: 'rentalspot.demo', flag: 'ES', dor: '2025-03-15', lor: 5, car: 'Ford Focus or similar', acriss: 'CDMR', transmission: 'Manual', category: 'Compact', mileage: 'Unlimited', provider: 'AutoRent', payment: 'Prepaid', price: 78.45 },
	{ website: 'alphacars.demo', flag: 'ES', dor: '2025-03-15', lor: 5, car: 'Peugeot 308 or similar', acriss: 'CDMR', transmission: 'Manual', category: 'Compact', mileage: 'Unlimited', provider: 'DrivePro', payment: 'Pay at desk', price: 82.60 },
	{ website: 'pricefly.demo', flag: 'ES', dor: '2025-03-15', lor: 5, car: 'Skoda Octavia or similar', acriss: 'IDMR', transmission: 'Manual', category: 'Intermediate', mileage: '1500 km', provider: 'PrimeFleet', payment: 'Prepaid', price: 89.30 },
	{ website: 'eurodrive.demo', flag: 'ES', dor: '2025-03-15', lor: 5, car: 'VW Golf or similar', acriss: 'CDMR', transmission: 'Automatic', category: 'Compact', mileage: 'Unlimited', provider: 'SpeedCar', payment: 'Prepaid', price: 95.10 },
	{ website: 'autolink.demo', flag: 'ES', dor: '2025-03-15', lor: 5, car: 'Mercedes A-Class or similar', acriss: 'CDAR', transmission: 'Automatic', category: 'Compact', mileage: 'Unlimited', provider: 'AutoRent', payment: 'Pay at desk', price: 102.80 },
	{ website: 'bookride.demo', flag: 'ES', dor: '2025-03-15', lor: 5, car: 'Peugeot 3008 or similar', acriss: 'SFAR', transmission: 'Automatic', category: 'SUV', mileage: 'Unlimited', provider: 'FastWheels', payment: 'Prepaid', price: 118.50 },
	{ website: 'rentalspot.demo', flag: 'ES', dor: '2025-03-15', lor: 5, car: 'BMW X1 or similar', acriss: 'IFAR', transmission: 'Automatic', category: 'Intermediate SUV', mileage: 'Unlimited', provider: 'RoadGo', payment: 'Pay at desk', price: 135.20 },
	{ website: 'findwheels.demo', flag: 'ES', dor: '2025-03-15', lor: 5, car: 'Audi Q3 or similar', acriss: 'SFAR', transmission: 'Automatic', category: 'SUV', mileage: 'Unlimited', provider: 'Velocar', payment: 'Pay at desk', price: 152.90 },
	{ website: 'pricefly.demo', flag: 'ES', dor: '2025-03-15', lor: 5, car: 'Mercedes C-Class or similar', acriss: 'FDAR', transmission: 'Automatic', category: 'Full-Size', mileage: 'Unlimited', provider: 'SpeedCar', payment: 'Prepaid', price: 178.40 },
	{ website: 'alphacars.demo', flag: 'ES', dor: '2025-03-15', lor: 5, car: 'Volvo XC60 or similar', acriss: 'PFAR', transmission: 'Automatic', category: 'Premium SUV', mileage: 'Unlimited', provider: 'DrivePro', payment: 'Pay at desk', price: 215.00 },
]

// The rows with the keys of the real results (Pages/RealTimeHistory/RealTime/@constants.js), so the
// table below is built with the page's own columns, toolbar, filters and hidden columns
const DEMO_ROWS = DEMO_RESULTS.map(r => ({
	id_site_translation: r.website,
	siteMarket: COUNTRY[r.flag],
	pickup_date_translation: r.dor,
	length_of_rental: r.lor,
	car_description: r.car,
	acriss_code: r.acriss,
	automatic_translation: r.transmission,
	id_category: r.category,
	unlimited_km_translation: r.mileage,
	id_master_provider_translation: r.provider,
	is_prepaid_translation: r.payment,
	price: r.price,
	id_master_currency_translation: 'EUR',
}))

// Searches that came back without prices, in the shape the status API gives them (errorTasksV2)
const DEMO_ERRORS = {
	noAvailData: { '2025-03-15': [{ siteName: 'swiftfleet.demo', siteCountry: 'Spain', info: [{ lor: 5 }] }] },
	errorData: { '2025-03-15': [{ siteName: 'horizoncars.demo', siteCountry: 'Spain', info: [{ lor: 5 }] }] },
}

// The heat scale of Pages/RealTimeHistory/RealTime/@hooks/useStatus.js over the demo prices (sorted cheapest first)
const FIRST_THIRD = Math.floor(DEMO_RESULTS.length / 3)
const SECOND_THIRD = FIRST_THIRD * 2
const DIFF = 10
const priceAt = idx => DEMO_RESULTS[idx].price
const HEAT = {
	success: new HeatColor({ min: priceAt(0) - DIFF, max: priceAt(FIRST_THIRD) + DIFF, color: dsColorNew.successBar, textColorWhite: dsColorNew.textDark, textColorDark: dsColorNew.textDark, inverse: true, maxLightness: 0.7 }),
	medium: new HeatColor({ min: priceAt(FIRST_THIRD) - DIFF, max: priceAt(SECOND_THIRD) + DIFF, color: dsColorNew.warningBar, textColorWhite: dsColorNew.textDark, textColorDark: dsColorNew.textDark, maxLightness: 0.7 }),
	error: new HeatColor({ min: priceAt(SECOND_THIRD) - DIFF, max: priceAt(DEMO_RESULTS.length - 1) + DIFF, color: dsColorNew.dangerRed, textColorWhite: dsColorNew.textDark, textColorDark: dsColorNew.textDark, maxLightness: 0.7 }),
}

const heatColor = (price, idx) => idx <= FIRST_THIRD
	? HEAT.success.getColor(Math.floor(price))
	: idx <= SECOND_THIRD ? HEAT.medium.getColor(price) : HEAT.error.getColor(price)

// the Website column's small grey link / screenshot buttons (they open nothing in the demo)
function SiteButton({ icon, tooltip }) {
	return <AppButtonIcon
		iconProps={{ sx: { color: dsColorNew.textMuted } }}
		sx={{ fontSize: 12, p: 0.75, ml: 0.25, '&:hover *': { color: `${dsColorNew.appBlue} !important` } }}
		icon={icon}
		tooltip={tooltip}
	/>
}

// Same cell as Table/@columns/website.jsx
const demoWebsiteColumn = {
	name: 'id_site_translation',
	label: 'Website',
	options: {
		setCellProps: () => ({ sx: { minWidth: '200px' } }),
		customBodyRowRenderLite: (value, { siteMarket }) => <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
			<AppRetailerBlock retailer={value} country={siteMarket}/>
			<SiteButton icon="up-right-from-square" tooltip="Link to Website"/>
			<SiteButton icon="image" tooltip="Screenshot"/>
		</Box>,
	},
}

// Same cell as Table/@columns/price.jsx: the heat colour in a pill
const demoPriceColumn = {
	name: 'price',
	label: 'Price',
	options: {
		filter: false,
		customBodyRowRenderLite: (value, { id_master_currency_translation }, idx) => <Box sx={{ textAlign: 'center', py: 1, px: 1 }}>
			<Box component="span" sx={{ display: 'inline-block', px: 1.25, py: 0.5, borderRadius: '6px', fontWeight: 600, fontSize: 13, ...heatColor(value, idx) }}>
				{formatPrice(value, id_master_currency_translation)}
			</Box>
		</Box>,
	},
}


// Same layout as Pages/RealTimeHistory/RealTime/Table: the summary of searches without prices, then the
// results table. Only while no real search is open (with ?idSearch= the real table is the one shown)
export default function RealTimeDemoData() {
	if (idSearch.value) return null

	return <Grid container spacing={2}>
		<Grid item xs={12}>
			<RealTimeTableErrorsData tableDataErrors={DEMO_ERRORS}/>
		</Grid>
		<Grid item xs={12}>
			<AppDataTable
				title={<RealTimeTableTitle timeStamp="2025-03-12 14:32:05"/>}
				data={DEMO_ROWS}
				// the toolbar of a real search, its buttons disabled so the demo launches nothing
				toolbarProps={{ enabledAnalyzers: true, realTimeSearchID: 'demo', directSite: true, brokers: true, disable: true }}
				columns={[
					demoWebsiteColumn,
					dorColumn(),
					...COLUMN_PARAMS_LIST.map(({ key, label, displayFilters = true, filterType = 'dropdown', display = true, providerLogo = false }) =>
						staticColumn(key, label, filterType, displayFilters, display, providerLogo)),
					demoPriceColumn,
				]}
				options={{
					viewColumns: true,
					search: true,
					enhancedSetRowProps: (rowData, dataIndex, idx) => ({
						sx: {
							backgroundColor: idx % 2 === 0 ? dsColorNew.white : dsColorNew.tableRowAlt,
							borderColor: dsColorNew.grayBorder,
							'& td': { padding: 0 },
						},
					}),
				}}
			/>
		</Grid>
	</Grid>
}
